@if (hasNotices()) {
<section
  class="fixed inset-x-0 bottom-0 z-50 shadow-2xl"
  aria-label="Results refresh operations"
  [attr.aria-expanded]="operations.panelExpanded()">
  <button
    type="button"
    class="flex w-full items-center justify-between gap-3 bg-[#c8d7ea] px-4 py-2 text-left text-[#1a3a5c] hover:bg-[#bdd0e6] focus:outline-2 focus:outline-offset-[-2px] focus:outline-[#1a3a5c] dark:bg-sky-900 dark:text-sky-50 dark:hover:bg-sky-800"
    [attr.aria-expanded]="operations.panelExpanded()"
    aria-controls="results-operations-list"
    (click)="toggleExpanded()">
    <p class="m-0 text-sm font-medium">
      Results refresh operations
      <span class="font-normal opacity-80"> · {{ headerSummary() }}</span>
    </p>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 20 20"
      fill="currentColor"
      [class]="chevronClass()"
      aria-hidden="true">
      <path fill-rule="evenodd" d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z" clip-rule="evenodd" />
    </svg>
  </button>

  @if (operations.panelExpanded()) {
  <ul id="results-operations-list" class="m-0 max-h-[40vh] list-none overflow-y-auto bg-[#202124] p-0 text-white dark:bg-zinc-950" role="list">
    @for (notice of operations.notices(); track notice.id) {
    <li class="border-t border-white/10 px-4 py-3" role="status" aria-live="polite">
      <div class="flex items-center gap-3">
        <div class="flex min-w-0 flex-1 items-center gap-3">
          @if (notice.status === 'running') {
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="size-4 shrink-0 animate-spin text-sky-400" aria-hidden="true">
            <path d="M21 12a9 9 0 1 1-6.219-8.56" />
          </svg>
          } @else if (notice.status === 'complete') {
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="size-4 shrink-0 text-emerald-400" aria-hidden="true">
            <path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z" clip-rule="evenodd" />
          </svg>
          } @else {
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="size-4 shrink-0 text-red-400" aria-hidden="true">
            <path fill-rule="evenodd" d="M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0Zm-8-5a.75.75 0 0 1 .75.75v4.5a.75.75 0 0 1-1.5 0v-4.5A.75.75 0 0 1 10 5Zm0 10a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z" clip-rule="evenodd" />
          </svg>
          }

          <div class="min-w-0">
            <p class="m-0 text-sm" [class]="rowStatusClass(notice.status)">
              {{ statusLabel(notice.status) }}
              <span class="text-white"> for </span>
              <span class="underline decoration-white/50 underline-offset-2">{{ notice.briefTitle }}</span>
            </p>
            @if (tallyLabel(notice)) {
            <p class="m-0 mt-1 text-xs text-white/70">{{ tallyLabel(notice) }}</p>
            }
            @if (notice.status === 'error' && notice.errorMessage) {
            <p class="m-0 mt-1 text-xs text-red-200">{{ notice.errorMessage }}</p>
            }
            @if (notice.outcomes.length > 0) {
            <button
              type="button"
              class="mt-1 text-xs text-sky-300 underline decoration-sky-300/40 underline-offset-2 hover:text-sky-200"
              (click)="operations.toggleDetailsExpanded(notice.id)">
              {{ notice.detailsExpanded ? 'Hide details' : 'Show details' }}
            </button>
            }
          </div>
        </div>

        <p class="m-0 shrink-0 text-sm tabular-nums text-white/80">{{ elapsedLabel(notice) }}</p>

        <button
          type="button"
          class="shrink-0 rounded-md p-1 text-white/60 hover:bg-white/10 hover:text-white focus:outline-2 focus:outline-offset-2 focus:outline-white"
          aria-label="Dismiss operation"
          (click)="operations.dismiss(notice.id)">
          <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" class="size-4">
            <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
          </svg>
        </button>
      </div>

      @if (notice.detailsExpanded && notice.outcomes.length > 0) {
      <ul class="m-0 mt-3 list-none space-y-2 border-t border-white/10 pt-3 pl-7" role="list">
        @for (outcome of notice.outcomes; track outcome.submittedPostId) {
        <li class="flex flex-wrap items-start gap-2 text-xs text-white/80">
          <span class="rounded px-1.5 py-0.5 font-medium uppercase tracking-wide" [class]="badgeClass(outcome)">
            {{ badgeLabel(outcome, notice) }}
          </span>
          <span class="min-w-0 flex-1">
            <span class="text-white">{{ outcome.creatorName || 'Creator' }}</span>
            <span class="text-white/50"> · {{ platformLabel(outcome) }}</span>
            @if (outcome.handle) {
            <span class="text-white/50"> · {{ outcome.handle }}</span>
            }
            @if (outcome.reason) {
            <span class="mt-0.5 block text-white/55">{{ outcome.reason }}</span>
            }
          </span>
        </li>
        }
      </ul>
      }
    </li>
    }
  </ul>
  }
</section>
}
